import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { isoDate } from "@/lib/format";
import { useScheduleConfirm } from "@/components/ScheduleConfirm";
import { useAvailability } from "@/components/ShiftsPanel";

export const ScheduleDialog = ({ open, onOpenChange, order, defaults, onDone }) => {
  const { teams, maps, projects } = useData();
  const [form, setForm] = useState({ team_id: "", employee_id: "", scheduled_date: "", slot: "mattina" });
  const [busy, setBusy] = useState(false);
  const [schedule, confirmDialog] = useScheduleConfirm();
  const [avail] = useAvailability(form.scheduled_date, form.scheduled_date);
  const note = (id) => avail[`${id}|${form.scheduled_date}`]?.label;

  useEffect(() => {
    if (open && order) {
      setForm({
        team_id: defaults?.team_id || order.team_id || "",
        employee_id: order.employee_id || "",
        scheduled_date: defaults?.scheduled_date || order.scheduled_date || order.appointment_date || isoDate(new Date()),
        slot: order.slot || "mattina",
      });
    }
  }, [open, order, defaults]);

  const team = maps.teams?.[form.team_id];
  const members = (team?.member_ids || []).map((id) => maps.employees?.[id]).filter(Boolean);
  const project = projects.find((p) => p.id === order?.project_id);
  const allowed = project ? [project.contract_company_id, ...(project.allowed_executing_company_ids || [])] : [];
  const eligible = teams.filter((t) => allowed.includes(t.company_id));

  const save = async () => {
    setBusy(true);
    try {
      const r = await schedule(order.id, { ...form, employee_id: form.employee_id || null });
      if (!r) return;
      toast.success(`Ordine ${order.external_order_id} pianificato`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const unschedule = async () => {
    try {
      await api.post(`/orders/${order.id}/unschedule`);
      toast.success("Assegnazione rimossa");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!order) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Pianifica <span className="font-mono">{order.external_order_id}</span></DialogTitle>
          <DialogDescription>Squadre ammesse dai rapporti contrattuali della commessa. Ogni assegnazione viene storicizzata.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label className="micro-label">Squadra</Label>
            <Select value={form.team_id || undefined} onValueChange={(v) => setForm({ ...form, team_id: v, employee_id: "" })}>
              <SelectTrigger className="mt-1.5" data-testid="schedule-team-select"><SelectValue placeholder="Seleziona squadra" /></SelectTrigger>
              <SelectContent>{eligible.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {maps.companies?.[t.company_id]?.code}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="micro-label">Tecnico</Label>
            <Select value={form.employee_id || undefined} onValueChange={(v) => setForm({ ...form, employee_id: v })} disabled={!members.length}>
              <SelectTrigger className="mt-1.5 min-w-0 [&>span]:truncate" data-testid="schedule-technician-select"><SelectValue placeholder="Capo squadra (predefinito)" /></SelectTrigger>
              <SelectContent>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · dip. {maps.companies?.[m.company_id]?.code} · {m.skills.join(", ")}{note(m.id) ? ` · ${note(m.id)}` : ""}</SelectItem>)}</SelectContent>
            </Select>
            {members.some((m) => note(m.id)) && (
              <div className="mt-2 break-words border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs" data-testid="schedule-availability-note">
                {members.filter((m) => note(m.id)).map((m) => <div key={m.id}><strong>{m.name}</strong>: {note(m.id)}</div>)}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_160px]">
            <div>
              <Label className="micro-label">Data</Label>
              <div className="mt-1.5"><DatePicker value={form.scheduled_date} onChange={(v) => setForm({ ...form, scheduled_date: v })} testId="schedule-date-picker" /></div>
            </div>
            <div>
              <Label className="micro-label">Fascia</Label>
              <Select value={form.slot} onValueChange={(v) => setForm({ ...form, slot: v })}>
                <SelectTrigger className="mt-1.5" data-testid="schedule-slot-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="mattina">Mattina</SelectItem><SelectItem value="pomeriggio">Pomeriggio</SelectItem><SelectItem value="sera">Sera</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          {order.status === "PLANNED" && <Button variant="outline" onClick={unschedule} data-testid="schedule-unassign-button">Rimuovi assegnazione</Button>}
          <Button onClick={save} disabled={busy || !form.team_id || !form.scheduled_date} data-testid="schedule-confirm-button">{busy ? "Salvataggio…" : "Conferma"}</Button>
        </DialogFooter>
      </DialogContent>
      {confirmDialog}
    </Dialog>
  );
};
